import {PdsWidget} from './pds-widget';
import {PoppableElementFactory} from './poppable-element-factory';
import {PopperPlacement} from './popper-configuration';
import {pdsUtils} from './pds-utils';

export class PdsTooltip extends PdsWidget {

  is = 'pds-tooltip';

  tooltipInstance: any;

  tooltipTrigger: HTMLElement | undefined;

  tooltipTriggerObserver: any | undefined;

  // a customizable direction.
  defaultDirection: PopperPlacement;

  // how we want our tooltip to show
  triggerAction: string;

  // string containing custom style, note that if you want to style using your own custom style
  // popper styles get appended with <custom-theme>-theme.

  customTheme: string;

  showHideDuration: [number, number];

  hasBeenAttached: boolean;

  displayValue: string | null;

  styleObserver: MutationObserver | undefined;

  //The pds-tooltip object is removed from the DOM!  If you need access to this, pass in a getReference function.
  getReference: (pdsTooltip: PdsTooltip) => {} | undefined;

  properties = {
    defaultDirection: {
      type: Object,
      value: 'bottom-start'
    },
    triggerAction: {
      type: String,
      value: 'mouseenter focus'
    },
    customTheme: {
      type: String,
      value: 'tooltip'
    },
    showHideDuration: {
      type: Object,
      value: [500, 0]
    }
  };

  //Polymer hook (this is kinda like a constructor)
  created(): void {
    this.hasBeenAttached = false;
  }

  /**
   * Polymer hook
   * the pds-is-hidden class is added to avoid the flicker effect that happens when angular and polymer runs through its change detection cycle.
   */
  ready(): void {
    super.ready();
    this.classList.add('pds-is-hidden');
  }

  //Polymer hook
  attached(): void {
    if (this.getReference != null) {
      this.getReference(this);
    }

    setTimeout(() => {
      super.attached();

      if (!this.hasBeenAttached) {
        //now that the initial test about being attached is complete, mark this instance of pds-tooltip as being attached.
        this.hasBeenAttached = true;

        //First time this element has been used. Gotta initialized it!
        this.tooltipTrigger = this.pds.utils.getClosest(this, '.pds-tooltip-trigger');

        //If there isn't any tooltip trigger, then this thing isn't going to work.
        if (this.tooltipTrigger == null) {
          console.error('No tooltip trigger detected.  An element with .pds-tooltip-trigger must be an ancestor of the pds-tooltip.');
          return;
        }

        let tooltipConfig = PoppableElementFactory.generateTooltipConfig(this.defaultDirection, this.triggerAction, this.asElement(), this.customTheme, this.showHideDuration);

        // by reason of using setTimeout, the remove operation might be executed when the element has been removed, make sure the classList is still available
        if (this.classList) {
          this.classList.remove('pds-is-hidden');
        }

        //generating the poppableElement takes 'this' DOM element out of the DOM.  It will be put back when the user
        //hovers over the tooltip trigger.
        this.tooltipInstance = PoppableElementFactory.generatePoppableElement(this.tooltipTrigger, tooltipConfig, this.is);

        // create a mutation observer and have it start tracking the style attribute - certain style changes should be propagated to the popper instance
        this.styleObserver = new MutationObserver(() => this.styleChanged());
        this.styleObserver.observe(this.asElement(), {
          attributes: true,
          attributeFilter: ['style']
        });

        // initialize the display value from the current display style so that changes can be tracked
        this.displayValue = this.asElement().style.display;
      } else {
        //The user just hovered over the trigger and tippy put it back in the DOM
        this.turnOnTooltipTriggerListener();
      }
    });
  }

  //polymer hook
  detached(): void {
    super.detached();
    //the PoppableElementFactory.generatePoppableElement() triggers this function to get called the first time.
    //So when it's called, this.tooltipInstance isn't set yet so it will be undefined.

    //no need to listen for the removal of .pds-tooltip-trigger if the pds-popover isn't in the dom.
    this.turnOffTooltipTriggerListener();
  }

  /**
   * Turns on a listener for the entire DOM for if the .pds-tooltip-trigger is removed from the DOM.
   * It is important for this listener to be on ONLY when tippy has turned on the tooltip.
   */
  turnOnTooltipTriggerListener(): void {
    if (this.tooltipTriggerObserver == null) {
      this.tooltipTriggerObserver = new MutationObserver((mutationsList, observer) => {
        if (this.tooltipTrigger != null && !document.contains(this.tooltipTrigger)) {
          //Time to remove the tooltip instance
          pdsUtils.remove(this.tooltipInstance.popper);

          // shut down the style observer while we're cleaning up
          if (this.styleObserver != null) {
            this.styleObserver.disconnect();
          }
        }
      });
    }

    this.tooltipTriggerObserver.observe(document, { childList: true, subtree: true });
  }

  /**
   * Turns off the .pds-tooltip-trigger listener.
   */
  turnOffTooltipTriggerListener(): void {
    if (this.tooltipTriggerObserver != null) {
      this.tooltipTriggerObserver.disconnect();
    }
  }

  /**
   * Propagates display value changes from the pds-tooltip instance to the popper instance.
   * This is needed so that the tooltip can work with polymer dom-if templates.
   */
  styleChanged(): void {
    // check if the display value has changed
    if (this.asElement().style.display !== this.displayValue) {
      // if it has, track the change
      this.displayValue = this.asElement().style.display;

      // then propagate the change to the popper instance
      this.tooltipInstance.popper.style.display = this.displayValue;
    }
  }

  updateDefaultDirection(tooltipDirection: PopperPlacement | null): void {
    if (tooltipDirection == null) {
      return;
    }

    this.defaultDirection = tooltipDirection;

    if (this.tooltipInstance != null) {
      if (this.tooltipInstance.popperInstance != null &&
          this.tooltipInstance.popperInstance.options != null) {
        this.tooltipInstance.popperInstance.options.placement = tooltipDirection;
      }

      if (this.tooltipInstance.options != null) {
        this.tooltipInstance.options.placement = tooltipDirection;
      }
    }
  }
}
